import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronRight, CheckCircle2, Sparkles, LocateFixed } from "lucide-react";
import { cn } from "@/lib/utils";
import { relDays } from "@/lib/format";

const KIND_LABEL = { avvik: "Avvik", service: "Service", transport: "Transport", konflikt: "Konflikt", oppgave: "Oppgave", tilbud: "Tilbud", henvendelse: "Henvendelse" };
const SEV_BAR = { danger: "bg-rose-500", warning: "bg-amber-500", info: "bg-sky-500" };
const BUCKETS = [
  { id: "kritisk", label: "Kritisk nå", hint: "høyest konsekvens – håndteres først" },
  { id: "i_dag", label: "Må avklares i dag", hint: "forfalt eller frist i dag" },
  { id: "denne_uken", label: "Denne uken", hint: "planlegg inn" },
  { id: "senere", label: "Til orientering", hint: "ingen frist ennå" },
];

const Item = ({ it, onLocate, canLocate }) => {
  const navigate = useNavigate();
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={() => navigate(it.url)}
        className="grid w-full grid-cols-[3px_1fr_auto] items-center gap-x-3 rounded-lg py-2 pl-2 pr-2 text-left transition-colors duration-150 hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none"
        data-testid={`attention-item-${it.kind}`}
      >
        <span className={cn("h-8 w-[3px] rounded-full", SEV_BAR[it.severity] || SEV_BAR.info)} aria-hidden />
        <span className="min-w-0 leading-tight">
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span>{KIND_LABEL[it.kind] || it.kind}</span>
            {it.ref && <span className="font-mono">{it.ref}</span>}
            {it.date && (
              <span className={cn("tabular", it.overdue && "text-amber-700 dark:text-amber-300")}>
                · {it.overdue ? "forfalt " : ""}
                {relDays(it.date)}
              </span>
            )}
          </span>
          <span className="block truncate text-[13px] leading-5">{it.title}</span>
          <span className="block truncate text-[11.5px] leading-4 text-muted-foreground">
            {it.project_name || it.meta}
            {it.action && (
              <span className={cn("inline-flex items-center gap-1", (it.project_name || it.meta) && "ml-1.5 before:mr-1.5 before:content-['·']", it.severity === "danger" ? "text-rose-600 dark:text-rose-400" : "text-primary")}>
                {it.action}
                <ArrowRight className="h-3 w-3" strokeWidth={2} aria-hidden />
              </span>
            )}
          </span>
        </span>
        <span className="flex items-center pr-6">
          <ChevronRight className="h-4 w-4 text-muted-foreground/60 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-foreground" />
        </span>
      </button>
      {canLocate && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onLocate(it.project_id);
          }}
          title="Vis på kartet"
          aria-label="Vis på kartet"
          className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity duration-150 hover:bg-card hover:text-primary focus-visible:opacity-100 focus-visible:outline-none group-hover:opacity-100"
          data-testid={`attention-locate-${it.kind}`}
        >
          <LocateFixed className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      )}
    </li>
  );
};

/**
 * «Prioritert nå» – the one list the operations lead reads first. Grouped Kritisk → I dag → Denne uken → Senere,
 * opened by an AI-phrased priority note. `variant="floating"` sits on the map; `"section"` stacks below it on
 * smaller screens. `locatable` is a Set of project ids that exist on the map.
 */
export const PriorityPanel = ({ attention, priority, priorityLoading, onLocate, locatable, variant = "floating", className }) => {
  const navigate = useNavigate();
  const items = attention?.items || [];
  const total = attention?.total ?? items.length;
  const sev = attention?.by_severity || {};
  const groups = BUCKETS.map((b) => ({ ...b, items: items.filter((i) => i.bucket === b.id) })).filter((g) => g.items.length);
  const floating = variant === "floating";

  return (
    <section
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-2xl",
        floating ? "h-full bg-card/90 shadow-pop backdrop-blur-xl dark:bg-card/80" : "bg-muted/40 ring-1 ring-black/[0.04] dark:bg-muted/25 dark:ring-white/5",
        className
      )}
      data-testid="dashboard-attention"
    >
      <header className="px-5 pb-3 pt-4">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-[14px] font-semibold leading-5 tracking-tight">Prioritert nå</h2>
          <span className="text-[12px] text-muted-foreground tabular" data-testid="dashboard-attention-total">
            {total} forhold
          </span>
        </div>
        <p className="mt-1.5 flex items-start gap-2 text-[12.5px] leading-5 text-foreground/75" data-testid="dashboard-priority-note">
          <Sparkles className="mt-[3px] h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={1.75} aria-hidden />
          {priorityLoading && !priority ? <span className="mt-1 h-3 w-[70%] animate-pulse rounded-full bg-muted" /> : <span>{priority || (total ? `${sev.danger || 0} kritiske · ${sev.warning || 0} bør følges opp · ${sev.info || 0} til orientering` : "Ingen forhold krever handling akkurat nå.")}</span>}
        </p>
        {total > 0 && (
          <div className="mt-3 flex h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden data-testid="dashboard-attention-meter">
            {["danger", "warning", "info"].map((k) => (sev[k] ? <span key={k} className={cn("h-full", SEV_BAR[k])} style={{ width: `${(sev[k] / total) * 100}%` }} /> : null))}
          </div>
        )}
      </header>

      {total === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center text-[12.5px] text-muted-foreground" data-testid="dashboard-attention-empty">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={1.75} />
          Alt er under kontroll – ingen forfalte oppgaver, avvik eller konflikter.
        </div>
      ) : (
        <div className={cn("min-h-0 flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin", !floating && "max-h-[520px]")} data-testid="dashboard-attention-list">
          {groups.map((g) => (
            <div key={g.id} data-testid={`attention-group-${g.id}`}>
              <div className={cn("sticky top-0 z-10 flex items-baseline justify-between px-3 pb-1 pt-2.5 text-[11px] font-medium text-muted-foreground backdrop-blur", floating ? "bg-card/90" : "bg-background/0")}>
                <span className="flex min-w-0 items-baseline gap-1.5">
                  <span className={cn(g.id === "kritisk" && "text-rose-600 dark:text-rose-400")}>{g.label}</span>
                  <span className="truncate font-normal text-muted-foreground/70">{g.hint}</span>
                </span>
                <span className="tabular">{g.items.length}</span>
              </div>
              <ul className="space-y-px">
                {g.items.map((it) => (
                  <Item key={it.id} it={it} onLocate={onLocate} canLocate={!!onLocate && !!it.project_id && (!locatable || locatable.has(it.project_id))} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <footer className="flex items-center justify-between gap-3 px-4 py-3 text-[12px]">
        <button type="button" onClick={() => navigate("/service")} className="inline-flex items-center gap-1 text-muted-foreground transition-colors duration-150 hover:text-foreground" data-testid="dashboard-attention-link">
          Alle saker <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
        {total > 0 && (
          <span className="truncate text-muted-foreground tabular">
            {sev.danger || 0} kritiske · {sev.warning || 0} bør følges opp
          </span>
        )}
      </footer>
    </section>
  );
};
